import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input } from "antd";
import { DownloadOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Title } = Typography;

const Check = () => <CheckCircleFilled style={{ color: "#52c41a", fontSize: "18px" }} />;
const Cross = () => <CloseCircleFilled style={{ color: "#f5222d", fontSize: "18px" }} />;
const Dash = () => <span style={{ color: "#999" }}>-</span>;

export default function SecondCheckList() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        fetchOrganisations();
        fetchEmployeeRecords();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            setDesignations([]);
        }
    };

    const fetchEmployeeRecords = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${backUrl}/api/reports/hr-documents/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setEmployeeData(res.data.results || []);
        } catch (error) {
            console.error("Error fetching employee records:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    /* ---------------- FILTERED & MAPPED TABLE DATA ---------------- */
    const tableData = useMemo(() => {
        let filtered = employeeData;

        // Apply filters
        if (selectedOrg) filtered = filtered.filter(item => item.organisation === selectedOrg);
        if (selectedDept) filtered = filtered.filter(item => item.department === selectedDept);
        if (selectedDesig) filtered = filtered.filter(item => item.designation === selectedDesig);

        // Apply search
        if (searchText) {
            const lowerSearch = searchText.toLowerCase();
            filtered = filtered.filter(item => {
                const empId = (item.emp_code || item.user?.username || "").toLowerCase();
                const name = `${item.first_name || ""} ${item.last_name || ""}`.trim().toLowerCase();
                return empId.includes(lowerSearch) || name.includes(lowerSearch);
            });
        }

        return filtered.map((item, index) => {
            const hasValue = (val) => val !== null && val !== undefined && val !== "";

            return {
                key: item.id,
                sno: index + 1,
                empId: item.emp_code || item.user?.username || "-",
                name: `${item.first_name || ""} ${item.last_name || ""}`.trim() || "-",

                // Personal Documents
                photograph: hasValue(item.photo) ? <Check /> : <Cross />,
                aadhaarCard: hasValue(item.aadhar_card_copy) || hasValue(item.aadhaar_card_number) ? <Check /> : <Cross />,
                panCard: hasValue(item.pan_card_copy) || hasValue(item.pan_card_number) ? <Check /> : <Cross />,
                bankAccount: hasValue(item.bank_account_details) || hasValue(item.bank_account_number) ? <Check /> : <Cross />,
                sslc: hasValue(item.SSLC) ? <Check /> : <Cross />,
                hscItiDiploma: hasValue(item.HSC_diploma_ITI) ? <Check /> : <Cross />,
                ugPg: hasValue(item.degree_certificate) || hasValue(item.PG_Phd) ? <Check /> : <Cross />,
                pf: hasValue(item.uan_number) ? <Check /> : <Cross />,
                esic: hasValue(item.esic_ip_number) ? <Check /> : <Cross />,

                // Onboarding Documents
                mrfForm: <Dash />,
                interviewAssessment: <Dash />,
                resume: <Dash />,
                bgvForm: <Dash />,
                offerLetter: hasValue(item.offer_letter) ? <Check /> : <Cross />,
                appointmentLetter: hasValue(item.appointment_letter) ? <Check /> : <Cross />,
                salaryAck: <Dash />,
                applicationForm: <Dash />,
                prevOfferLetter: <Dash />,
                prevAppointmentLetter: <Dash />,
                relievingOrder: hasValue(item.releiving_letter) ? <Check /> : <Cross />,
                prevPayslips: hasValue(item.last_three_month_payslip) ? <Check /> : <Cross />,
                prevAccountSummary: hasValue(item.last_three_months_account_statement) ? <Check /> : <Cross />,

                // Orientation Process
                biometricAccess: <Dash />,
                accessCard: <Dash />,
                emailId: hasValue(item.official_email_id) ? <Check /> : <Cross />,
                laptopDesktop: <Dash />,
                tempIdCard: <Dash />,
                permanentIdCard: <Dash />,
                mobileCug: hasValue(item.official_cug_number) ? <Check /> : <Cross />,
                simCard: hasValue(item.official_cug_number) ? <Check /> : <Cross />,
                businessCard: <Dash />,
                erpId: hasValue(item.user?.username) || hasValue(item.emp_code) ? <Check /> : <Cross />,
                uniform: <Dash />,
                shoes: <Dash />,
                welcomeKit: <Dash />,
                workStation: <Dash />,
                bankKit: <Dash />,

                remark: "" // Can be extended later
            };
        });
    }, [employeeData, selectedOrg, selectedDept, selectedDesig, searchText]);

    /* ---------------- TABLE COLUMNS ---------------- */
    const columns = [
        {
            title: "SNO",
            dataIndex: "sno",
            key: "sno",
            width: 70,
            fixed: "left"
        },
        {
            title: "Emp ID",
            dataIndex: "empId",
            key: "empId",
            width: 120,
            fixed: "left"
        },
        {
            title: "Employee Name",
            dataIndex: "name",
            key: "name",
            width: 180,
            fixed: "left"
        },
        {
            title: "Personal Documents",
            children: [
                { title: "Passport size photograph", dataIndex: "photograph", key: "photograph", width: 140, align: "center" },
                { title: "Aadhaar card", dataIndex: "aadhaarCard", key: "aadhaarCard", width: 120, align: "center" },
                { title: "PAN card", dataIndex: "panCard", key: "panCard", width: 110, align: "center" },
                { title: "Bank account", dataIndex: "bankAccount", key: "bankAccount", width: 120, align: "center" },
                { title: "10TH", dataIndex: "sslc", key: "sslc", width: 100, align: "center" },
                { title: "12TH/ITI/DIPLOMA", dataIndex: "hscItiDiploma", key: "hscItiDiploma", width: 140, align: "center" },
                { title: "UG/PG", dataIndex: "ugPg", key: "ugPg", width: 100, align: "center" },
                { title: "PF", dataIndex: "pf", key: "pf", width: 80, align: "center" },
                { title: "ESI", dataIndex: "esic", key: "esic", width: 80, align: "center" },
            ]
        },
        {
            title: "Onboarding Documents",
            children: [
                { title: "MRF Form", dataIndex: "mrfForm", key: "mrfForm", width: 120, align: "center" },
                { title: "Interview Assessment Sheet", dataIndex: "interviewAssessment", key: "interviewAssessment", width: 160, align: "center" },
                { title: "Resume", dataIndex: "resume", key: "resume", width: 100, align: "center" },
                { title: "Background Verification Form", dataIndex: "bgvForm", key: "bgvForm", width: 180, align: "center" },
                { title: "Offer letter", dataIndex: "offerLetter", key: "offerLetter", width: 120, align: "center" },
                { title: "Appointment Letter", dataIndex: "appointmentLetter", key: "appointmentLetter", width: 140, align: "center" },
                { title: "Salary Acknowledgement", dataIndex: "salaryAck", key: "salaryAck", width: 160, align: "center" },
                { title: "Application form", dataIndex: "applicationForm", key: "applicationForm", width: 130, align: "center" },
                { title: "Previous company Offer letter", dataIndex: "prevOfferLetter", key: "prevOfferLetter", width: 180, align: "center" },
                { title: "Previous company Appointment letter", dataIndex: "prevAppointmentLetter", key: "prevAppointmentLetter", width: 200, align: "center" },
                { title: "Previous company Relieving order", dataIndex: "relievingOrder", key: "relievingOrder", width: 180, align: "center" },
                { title: "Previous company Payslip's", dataIndex: "prevPayslips", key: "prevPayslips", width: 160, align: "center" },
                { title: "Previous company Salary Credited Account summary", dataIndex: "prevAccountSummary", key: "prevAccountSummary", width: 220, align: "center" },
            ]
        },
        {
            title: "Orientation Process",
            children: [
                { title: "Biometric Access", dataIndex: "biometricAccess", key: "biometricAccess", width: 140, align: "center" },
                { title: "Access Card", dataIndex: "accessCard", key: "accessCard", width: 120, align: "center" },
                { title: "E-mail ID", dataIndex: "emailId", key: "emailId", width: 120, align: "center" },
                { title: "Laptop/Desktop", dataIndex: "laptopDesktop", key: "laptopDesktop", width: 140, align: "center" },
                { title: "Temporary Id Card", dataIndex: "tempIdCard", key: "tempIdCard", width: 140, align: "center" },
                { title: "Permanent ID card", dataIndex: "permanentIdCard", key: "permanentIdCard", width: 140, align: "center" },
                { title: "Mobile", dataIndex: "mobileCug", key: "mobileCug", width: 100, align: "center" },
                { title: "CUG Sim Card", dataIndex: "simCard", key: "simCard", width: 120, align: "center" },
                { title: "Business Card", dataIndex: "businessCard", key: "businessCard", width: 120, align: "center" },
                { title: "ERP ID", dataIndex: "erpId", key: "erpId", width: 100, align: "center" },
                { title: "Uniform", dataIndex: "uniform", key: "uniform", width: 100, align: "center" },
                { title: "Shoes", dataIndex: "shoes", key: "shoes", width: 100, align: "center" },
                { title: "Welcome Kit's", dataIndex: "welcomeKit", key: "welcomeKit", width: 120, align: "center" },
                { title: "Work Station / cabin", dataIndex: "workStation", key: "workStation", width: 150, align: "center" },
                { title: "Bank Kit's", dataIndex: "bankKit", key: "bankKit", width: 120, align: "center" },
            ]
        },
        {
            title: "Remarks",
            dataIndex: "remark",
            key: "remark",
            width: 200
        }
    ];

    /* ---------------- EXCEL DOWNLOAD ---------------- */
    const downloadExcel = () => {
        const excelData = tableData.map(row => ({
            "SNO": row.sno,
            "Emp ID": row.empId,
            "Employee Name": row.name,

            // Personal Documents
            "Passport size photograph": row.photograph?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Aadhaar card": row.aadhaarCard?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "PAN card": row.panCard?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Bank account": row.bankAccount?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "10TH": row.sslc?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "12TH/ITI/DIPLOMA": row.hscItiDiploma?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "UG/PG": row.ugPg?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "PF": row.pf?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "ESI": row.esic?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",

            // Onboarding Documents
            "MRF Form": "-",
            "Interview Assessment Sheet": "-",
            "Resume": "-",
            "Background Verification Form": "-",
            "Offer letter": row.offerLetter?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Appointment Letter": row.appointmentLetter?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Salary Acknowledgement": "-",
            "Application form": "-",
            "Previous company Offer letter": "-",
            "Previous company Appointment letter": "-",
            "Previous company Relieving order": row.relievingOrder?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Previous company Payslip's": row.prevPayslips?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Previous company Salary Credited Account summary": row.prevAccountSummary?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",

            // Orientation Process
            "Biometric Access": "-",
            "Access Card": "-",
            "E-mail ID": row.emailId?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Laptop/Desktop": "-",
            "Temporary Id Card": "-",
            "Permanent ID card": "-",
            "Mobile": row.mobileCug?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "CUG Sim Card": row.simCard?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Business Card": "-",
            "ERP ID": row.erpId?.props?.children?.type === CheckCircleFilled ? "YES" : "NO",
            "Uniform": "-",
            "Shoes": "-",
            "Welcome Kit's": "-",
            "Work Station / cabin": "-",
            "Bank Kit's": "-",

            "Remarks": row.remark
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Checklist Report");

        // Header styling
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                fill: { fgColor: { rgb: "0e497b" } },
                font: { color: { rgb: "FFFFFF" }, bold: true },
                alignment: { horizontal: "center", wrapText: true }
            };
        }

        worksheet["!cols"] = Array(Object.keys(excelData[0] || {}).length).fill({ wch: 20 });

        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "BAASHYAAM_Employee_Onboarding_Checklist_Report.xlsx");
    };

    return (
        <>
            <Title level={4}>Checklist 2</Title>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={5}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={tableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: tableData.length,
                    onChange: page => setCurrentPage(page),
                    showSizeChanger: false
                }}
                scroll={{ x: 5000 }}
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </>
    );
}